import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useRequest } from "../../hooks/useRequest";
import { useLocation, useNavigate } from "react-router-dom";
import GroupBuyGrid from "../../components/common/GroupBuyGrid";
import FilterableProducts from "../../components/search/FilterableProducts";
import { useAppContext } from "../../context/useAppContext";
import CategorySlider from "../../components/category/CategorySlider";
import TrendingProductsGrid from "../../components/common/TrendingProductsGrid";
import SlidingBanner from "../../components/common/SlidingBanner";
import { Skeleton } from "@mui/material";
import SEO from "../../components/seo/SEO";

const CategoryStyle = styled.div`
  .height_container {
    height: 50px;
  }
  .categories {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 40px;
    padding: 0 0;
  }
  .products__related {
    padding: 50px 0;
  }
  @media (max-width: 768px) {
    .categories {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
      padding: 40px 0;
    }
    .products__related {
      padding: 30px 0 50px;
    }
  }
`;

const Search = ({ showCategory = true, showProducts = true, showRelated = true }) => {
  const { categoriesTree, categoriesLoading } = useAppContext();
  const [brandsData, setBrandsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [fetchCategories, { isLoading: isFetchingCategories }] = useRequest();
  const [fetchBrands] = useRequest();
  const { search } = useLocation();
  const navigate = useNavigate();
  const [categoriesIdList, setCategoriesIdList] = useState();
  const [categoryNames, setCategoryNames] = useState([]);
  const [brandNames, setBrandNames] = useState([]);
  const searchParams = new URLSearchParams(search);

  const categoryId = searchParams.get("categories[]");
  const searchTerm = searchParams.get("search");
  const categoryIds = searchParams.getAll("categories[]");
  const brandIds = searchParams.getAll("brands[]");

  useEffect(() => {
    (async function () {
      const shouldSendCategoryId = categoryId && categoryId !== "interior" && categoryId !== "exterior";
      if (shouldSendCategoryId) {
        const categoryData = await fetchCategories({
          path: `/category/categories/json-tree-updated?${shouldSendCategoryId ? `categoryId=${categoryId}` : ""}`,
        });
        setCategoriesData(categoryData?.data);
      } else {
        setCategoriesData(categoriesTree);
      }
      const brandData = await fetchBrands({
        path: `/brand?limit=100&page=1`,
      });

      setBrandsData(brandData?.data?.docs);

      // Fetch category names for SEO
      if (categoryIds.length > 0) {
        const categoryPromises = categoryIds.map(id =>
          fetchCategories({ path: `/category/${id}` })
        );
        const categoryResults = await Promise.all(categoryPromises);
        const names = categoryResults
          .map(result => result?.data?.name)
          .filter(Boolean);
        setCategoryNames(names);
      } else {
        setCategoryNames([]);
      }

      // Fetch brand names for SEO
      if (brandIds.length > 0 && brandData?.data?.docs) {
        const names = brandData.data.docs
          .filter(brand => brandIds.includes(brand._id))
          .map(brand => brand.name)
          .filter(Boolean);
        setBrandNames(names);
      } else {
        setBrandNames([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, categoriesTree, search]);

  const handleCategoryClick = (item) => {
    if (!categoriesIdList?.includes(item?._id)) {
      searchParams.delete("previousCategoryId");
      if (categoryId) {
        searchParams.append("previousCategoryId", categoryId);
      } else {
        searchParams.append("previousCategoryId", item?._id);
      }
      searchParams.delete("categories[]");
      searchParams.append("categories[]", item?._id);
      const newUrl = `/search?${searchParams.toString()}`;
      navigate(newUrl);
    }
  };

  const [fetchSlidingBanner, { isLoading: isFetchingSlidingBanner, state: slidingBanners }] = useRequest(
    `/instabuildslider?limit=10&page=1`
  );

  useEffect(() => {
    fetchSlidingBanner();
  }, []);

  const getSearchTitle = () => {
    const parts = [];

    if (searchTerm) {
      parts.push(searchTerm);
    }

    if (categoryNames.length > 0) {
      parts.push(categoryNames.join(", "));
    }

    if (brandNames.length > 0) {
      parts.push(brandNames.join(", "));
    }

    if (search.includes("instabuild=true")) {
      parts.push("InstabuildPro Products");
    } else if (search.includes("clearance=true")) {
      parts.push("Clearance");
    } else if (search.includes("group")) {
      parts.push("Group Buy");
    }

    if (parts.length > 0) {
      return `${parts.join(" - ")} | Building Materials | Instabuild`;
    }

    return "Building Materials & Construction Products | Instabuild";
  };

  const getSearchDescription = () => {
    const parts = [];

    if (searchTerm) {
      parts.push(`Search results for "${searchTerm}"`);
    }

    if (categoryNames.length > 0) {
      parts.push(`Shop ${categoryNames.join(", ")} building materials`);
    }

    if (brandNames.length > 0) {
      parts.push(`from ${brandNames.join(", ")}`);
    }

    if (search.includes("instabuild=true")) {
      parts.push("Browse InstabuildPro premium building materials and construction products");
    } else if (search.includes("clearance=true")) {
      parts.push("Shop clearance building materials at discounted prices");
    } else if (search.includes("group")) {
      parts.push("Join group buy deals on building materials");
    }

    if (parts.length > 0) {
      return `${parts.join(". ")}. Quality roofing, lumber, tools, insulation, and more at Instabuild.`;
    }

    return "Search and shop quality building materials, construction products, interior & exterior supplies. Find roofing, lumber, tools, insulation, drywall, and more at Instabuild.";
  };

  const getSearchKeywords = () => {
    const keywords = [];

    if (searchTerm) {
      keywords.push(searchTerm);
    }

    if (categoryNames.length > 0) {
      keywords.push(...categoryNames);
    }

    if (brandNames.length > 0) {
      keywords.push(...brandNames);
    }

    keywords.push(
      "building materials",
      "construction supplies",
      "roofing materials",
      "lumber",
      "tools",
      "insulation",
      "drywall",
      "building products",
      "construction products"
    );

    if (search.includes("instabuild=true")) {
      keywords.push("InstabuildPro");
    }
    if (search.includes("clearance=true")) {
      keywords.push("clearance", "discounted");
    }
    if (search.includes("group")) {
      keywords.push("group buy", "bulk purchase");
    }

    return keywords.join(", ");
  };

  return (
    <CategoryStyle className="container">
      <SEO
        title={getSearchTitle()}
        description={getSearchDescription()}
        keywords={getSearchKeywords()}
      />
      {search.includes("instabuild=true") && (
        <>
          <div className="height_container"></div>
          <SlidingBanner
            reqHeight={180}
            onlyBanner={true}
            bannerData={slidingBanners?.data?.docs}
            leftdistance={30}
            loading={isFetchingSlidingBanner || !slidingBanners?.data?.docs}
          />
        </>
      )}
      <CategorySlider handleCategoryClick={handleCategoryClick} />

      {/* {showProducts && (
        <>
          {isFetchingCategories || categoriesLoading ? (
            <Skeleton variant="rectangular"  width={"100%"} height={400} />
          ) : ( */}
            <FilterableProducts
              categoriesData={categoriesData}
              brandsData={brandsData}
              setCategoriesList={setCategoriesIdList}
            /> 
      {/* )}
          </>
      )} */}

      {showRelated && !search.includes("instabuild=true") && (
        <div className="products__related">
          <GroupBuyGrid />
        </div>
      )}
      <div className="container products__related">
        <TrendingProductsGrid
          heading={`Top Selling ${search.includes("instabuild=true") ? "Instabuild" : ""} Products`}
          api={"/product/top-selling"}
        />
      </div>
      <div className="height_container"></div>
    </CategoryStyle>
  );
};

export default Search;
